"use client";

import { useState } from "react";
import Link from "next/link";
import { useLang } from "@/contexts/language-context";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import AnimatedSection, { AnimatedElement } from "./AnimatedSection";
import FeatureDetailModal from "./FeatureDetailModal";
import { getFeaturesByVertical, type MarketingFeature, type Vertical } from "@/lib/marketing/features-data";

export default function FeaturesIndexClient() {
  const { t } = useLang();
  const [openFeature, setOpenFeature] = useState<MarketingFeature | null>(null);
  const [activeVertical, setActiveVertical] = useState<Vertical>("restaurant");

  const restaurantFeatures = getFeaturesByVertical("restaurant");
  const nightlifeFeatures = getFeaturesByVertical("nightlife");
  const featuresToShow =
    activeVertical === "restaurant" ? restaurantFeatures : nightlifeFeatures;

  const isDark = activeVertical === "nightlife";

  return (
    <>
      {/* Hero */}
      <section
        className={`relative pt-32 pb-16 px-6 overflow-hidden transition-colors duration-500 ${
          isDark ? "bg-[#0A0814] text-white" : "bg-white text-redy-charcoal"
        }`}
      >
        {isDark ? (
          <>
            <div className="absolute top-0 right-0 w-[700px] h-[700px] bg-[#7C3AED]/22 rounded-full blur-[160px] pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#F472B6]/15 rounded-full blur-[140px] pointer-events-none" />
          </>
        ) : (
          <>
            <div className="absolute top-0 right-0 w-[700px] h-[700px] bg-redy-orange/6 rounded-full blur-[140px] pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-redy-red/5 rounded-full blur-[100px] pointer-events-none" />
          </>
        )}

        <div className="relative max-w-[1200px] mx-auto text-center">
          <AnimatedSection>
            <div
              className={`text-[11px] font-bold tracking-widest uppercase mb-4 ${
                isDark ? "text-[#C4B5FD]" : "text-redy-orange"
              }`}
            >
              {t("Every feature in Redy", "Cada feature de Redy")}
            </div>
            <h1
              className={`text-4xl md:text-[68px] font-black tracking-tight leading-[1.02] mb-6 ${
                isDark ? "text-white" : "text-redy-charcoal"
              }`}
            >
              {t("19 features.", "19 features.")}
              <br />
              <span className={isDark ? "text-white/30" : "text-black/30"}>
                {t("One platform.", "Una plataforma.")}
              </span>
            </h1>
            <p
              className={`text-[15px] md:text-[17px] font-medium max-w-[640px] mx-auto leading-relaxed mb-10 ${
                isDark ? "text-white/60" : "text-redy-secondary"
              }`}
            >
              {t(
                "Click any feature for a quick overview, or open the full page for execution steps and screenshots.",
                "Toca cualquier feature para un resumen rápido, o abre la página completa con pasos de ejecución y screenshots."
              )}
            </p>

            {/* Vertical toggle */}
            <div
              className={`inline-flex p-1.5 rounded-full ${
                isDark ? "bg-white/5 border border-white/10" : "bg-[#FAFAFA] border border-black/5"
              }`}
            >
              <button
                onClick={() => setActiveVertical("restaurant")}
                className={`relative px-6 py-2.5 rounded-full text-[13px] font-bold transition-colors cursor-pointer ${
                  activeVertical === "restaurant"
                    ? "text-white"
                    : isDark
                    ? "text-white/55 hover:text-white"
                    : "text-redy-charcoal/55 hover:text-redy-charcoal"
                }`}
              >
                {activeVertical === "restaurant" && (
                  <motion.div
                    layoutId="vertical-pill"
                    className="absolute inset-0 bg-redy-charcoal rounded-full"
                    transition={{ type: "spring", stiffness: 400, damping: 30 }}
                  />
                )}
                <span className="relative">{t("Restaurant", "Restaurant")}</span>
              </button>
              <button
                onClick={() => setActiveVertical("nightlife")}
                className={`relative px-6 py-2.5 rounded-full text-[13px] font-bold transition-colors cursor-pointer ${
                  activeVertical === "nightlife"
                    ? "text-white"
                    : isDark
                    ? "text-white/55 hover:text-white"
                    : "text-redy-charcoal/55 hover:text-redy-charcoal"
                }`}
              >
                {activeVertical === "nightlife" && (
                  <motion.div
                    layoutId="vertical-pill"
                    className="absolute inset-0 rounded-full"
                    style={{ backgroundImage: "linear-gradient(135deg, #7C3AED 0%, #F472B6 100%)" }}
                    transition={{ type: "spring", stiffness: 400, damping: 30 }}
                  />
                )}
                <span className="relative">{t("Nightlife", "Nightlife")}</span>
              </button>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Feature grid */}
      <section
        className={`py-20 px-6 transition-colors duration-500 ${
          isDark ? "bg-[#0F0B1F]" : "bg-[#FAFAFA]"
        }`}
      >
        <div className="max-w-[1200px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuresToShow.map((feat, i) => {
              const Icon = feat.icon;
              const accent = feat.accent ?? (isDark ? "#C4B5FD" : "#F58A1F");
              return (
                <AnimatedElement
                  key={feat.slug}
                  delay={0.04 * i}
                  className={`rounded-[28px] hover:-translate-y-1 transition-all group focus-within:ring-2 ${
                    isDark
                      ? "bg-white/[0.04] border border-white/10 hover:bg-white/[0.06] hover:border-white/15 focus-within:ring-[#7C3AED]/40"
                      : "bg-white border border-black/5 shadow-sm hover:shadow-md focus-within:ring-redy-orange/30"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => setOpenFeature(feat)}
                    className="block w-full h-full text-left p-7 outline-none cursor-pointer"
                  >
                    <div className="flex justify-between items-start mb-5">
                      <div
                        className={`w-12 h-12 rounded-[16px] flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform ${
                          isDark ? "" : "bg-[#FAFAFA] border border-black/5"
                        }`}
                        style={{ backgroundColor: isDark ? `${accent}22` : undefined }}
                      >
                        <Icon className="w-5 h-5" style={{ color: accent }} />
                      </div>
                    </div>
                    <h3
                      className={`text-[18px] font-black mb-2 ${
                        isDark ? "text-white" : "text-redy-charcoal"
                      }`}
                    >
                      {t(feat.card.title.en, feat.card.title.es)}
                    </h3>
                    <p
                      className={`text-[14px] font-medium leading-relaxed mb-4 ${
                        isDark ? "text-white/55" : "text-redy-secondary"
                      }`}
                    >
                      {t(feat.card.desc.en, feat.card.desc.es)}
                    </p>
                    <span
                      className="inline-flex items-center gap-1 text-[12px] font-bold opacity-60 group-hover:opacity-100 transition-opacity"
                      style={{ color: accent }}
                    >
                      {t("Quick view", "Vista rápida")} →
                    </span>
                  </button>
                  <Link
                    href={`/features/${feat.slug}`}
                    className={`block px-7 py-4 border-t text-[12px] font-bold transition-colors ${
                      isDark
                        ? "border-white/10 text-white/55 hover:text-white"
                        : "border-black/5 text-redy-secondary hover:text-redy-charcoal"
                    }`}
                  >
                    {t("Open full page", "Abrir página completa")} →
                  </Link>
                </AnimatedElement>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        className={`relative py-24 px-6 overflow-hidden transition-colors duration-500 ${
          isDark ? "bg-[#0A0814] text-white" : "bg-white text-redy-charcoal"
        }`}
      >
        {isDark && (
          <>
            <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-[#7C3AED]/22 rounded-full blur-[140px] pointer-events-none" />
            <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-[#F472B6]/18 rounded-full blur-[140px] pointer-events-none" />
          </>
        )}

        <div className="relative max-w-[760px] mx-auto text-center">
          <AnimatedSection>
            <h2
              className={`text-3xl md:text-[44px] font-black tracking-tight leading-[1.1] mb-6 ${
                isDark ? "text-white" : "text-redy-charcoal"
              }`}
            >
              {t("All of this. From $0/month.", "Todo esto. Desde $0/mes.")}
            </h2>
            <p
              className={`text-[15px] md:text-[17px] font-medium max-w-[520px] mx-auto leading-relaxed mb-10 ${
                isDark ? "text-white/60" : "text-redy-secondary"
              }`}
            >
              {t(
                "Month to month, no long-term contract. Start free when you process payments with Redy; some features need Plus or Pro. Card payments go live once your payments account is approved.",
                "Mes a mes, sin contrato a largo plazo. Empieza gratis si procesas tus pagos con Redy; algunas features requieren Plus o Pro. Los cobros con tarjeta se activan cuando se aprueba tu cuenta de pagos."
              )}
            </p>
            <Link
              href="/signup"
              className={`group inline-flex items-center gap-2 text-[15px] font-bold px-8 py-4 rounded-full transition-all active:scale-95 text-white shadow-lg ${
                isDark ? "" : "bg-redy-charcoal hover:bg-black"
              }`}
              style={
                isDark
                  ? { backgroundImage: "linear-gradient(135deg, #7C3AED 0%, #F472B6 100%)" }
                  : undefined
              }
            >
              {t("Get started free", "Comenzar gratis")}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </AnimatedSection>
        </div>
      </section>

      <FeatureDetailModal feature={openFeature} onClose={() => setOpenFeature(null)} />
    </>
  );
}
